import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import { API_BASE_URL } from '../api';
import { canWriteTestSuites } from '../permissions';
import './DashboardPage.css';

/** A single project and the test suites under it. */
function ProjectDetailPage({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [suites, setSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const canCreateSuite = canWriteTestSuites(user);
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [projectRes, suitesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/suites/project/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (projectRes.status === 404) {
        setError('This project could not be found.');
        return;
      }
      if (projectRes.ok) setProject(await projectRes.json());
      if (suitesRes.ok) setSuites(await suitesRes.json());
    } catch (err) {
      console.error('Error loading project:', err);
      setError('Something went wrong loading this project.');
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <div className="dashboard-page"><div className="container"><p className="loading">Loading...</p></div></div>;

  return (
    <div className="dashboard-page">
      <div className="container">
        <Breadcrumb
          items={[
            { label: 'Projects', to: '/projects' },
            { label: project?.name || `Project ${projectId}` },
          ]}
        />

        {error && <div className="error-banner">{error}</div>}

        {project && (
          <>
            <div className="dashboard-title-row">
              <div>
                <h1>{project.name}</h1>
                <p className="welcome">{project.description || 'No description'}</p>
              </div>
              {canCreateSuite && (
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/projects/${projectId}/suites/new`)}
                >
                  + New Suite
                </button>
              )}
            </div>

            <div className="dashboard-projects-col">
              <h2 className="col-heading">Test Suites</h2>
              {suites.length === 0 ? (
                <div className="empty-state">
                  <p>No test suites yet.{canCreateSuite ? ' Create one to start adding test cases.' : ''}</p>
                </div>
              ) : (
                <div className="projects-list">
                  {suites.map((suite) => (
                    <div key={suite.id} className="project-row">
                      <div className="project-row-main">
                        <div className="project-row-name">{suite.name}</div>
                        <div className="project-row-desc">{suite.description || 'No description'}</div>
                      </div>
                      <Link
                        to={`/projects/${projectId}/suites/${suite.id}`}
                        className="btn btn-secondary btn-small project-row-open"
                      >
                        Open
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ProjectDetailPage;
